import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { toast } from "react-toastify";
import * as dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { Grid, IconButton, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";

import { accountStatementColumns } from "constants/columns.constants";
import { downloadAccountStatementService } from "services/users.service";
import { formatDate } from "helpers/formatData";

import DownloadIcon from "@mui/icons-material/Download";

dayjs.extend(utc);

const AccountStatement = ({ userId }) => {
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [durations, setDurations] = useState({
    startDate: null,
    endDate: null,
  });

  const [isFileExist, setFileExist] = useState(false);
  const [fileResponse, setFileResponse] = useState(null);

  const onDownloadStatement = async () => {
    if (!fileResponse) {
      toast.error("Sorry, Could not download file!");
      return;
    }
    try {
      const blob = new Blob([fileResponse.data], {
        type: "application/pdf;charset=utf-8;",
      });
      setFileExist(true);

      const url = window.URL.createObjectURL(blob);
      const [, filename] =
        fileResponse.headers["content-disposition"]?.split("filename=");

      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute("href", url);
      a.setAttribute("download", filename ?? "Account-Statement.pdf");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      const formattedError = JSON.parse(
        String.fromCharCode.apply(
          null,
          Array.from(new Uint8Array(error?.response?.data))
        )
      );
      toast.error(
        formattedError?.response?.data?.message ?? "Something went wrong!"
      );
    }
  };

  const columns = useMemo(() => {
    return accountStatementColumns;
  }, []);

  const rows = useMemo(() => {
    return fileResponse
      ? [
          {
            date: formatDate(dayjs()),
            statement: `${formatDate(
              durations.startDate,
              "MMM DD, YYYY"
            )}- ${formatDate(durations.endDate, "MMM DD, YYYY")}`,
            download: (
              <IconButton onClick={onDownloadStatement}>
                <DownloadIcon />
              </IconButton>
            ),
          },
        ]
      : [];
  }, [fileResponse, isFileExist, durations]);

  const onApplyClick = async () => {
    try {
      if (!startDate || !endDate) {
        toast.warn(`Please select start date and end date to Apply filter`);
        return;
      }
      const fromDate = dayjs(startDate).startOf("day").utc(true).format();
      const toDate = dayjs(endDate).endOf("day").utc(true).format();

      setLoading(true);
      const response = await downloadAccountStatementService(userId, {
        startDate: fromDate,
        endDate: toDate,
      });
      setDurations({
        startDate: fromDate,
        endDate: endDate,
      });
      setFileResponse(response);
      setFileExist(true);
    } catch (error) {
      setFileResponse(null);
      setDurations({
        startDate: null,
        endDate: null,
      });
      setFileExist(false);
      const formattedError = JSON.parse(
        String.fromCharCode.apply(
          null,
          Array.from(new Uint8Array(error?.response?.data))
        )
      );
      toast.error(formattedError?.message ?? "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const onClearClick = () => {
    setStartDate(null);
    setEndDate(null);
    setDurations({ startDate: null, endDate: null });
    setFileResponse(null);
    setFileExist(true);
  };

  return (
    <>
      <Grid container spacing={4} p={3}>
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <LocalizationProvider
            sx={{
              width: "100%",
            }}
            dateAdapter={AdapterDayjs}
          >
            <DatePicker
              label="Start Date"
              value={startDate}
              disableFuture
              onChange={(value) => {
                setStartDate(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={dayjs(startDate).isAfter(endDate)}
                  helperText={
                    dayjs(startDate).isAfter(endDate)
                      ? "Start Date can not be greater than End Date!"
                      : ""
                  }
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <LocalizationProvider
            sx={{
              width: "100%",
            }}
            dateAdapter={AdapterDayjs}
          >
            <DatePicker
              label="End Date"
              minDate={startDate}
              disableFuture
              value={endDate}
              onChange={(value) => {
                setEndDate(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <MDButton
            color="info"
            isLoading={loading}
            onClick={onApplyClick}
            disabled={
              loading ||
              !startDate ||
              !endDate ||
              dayjs(endDate).isBefore(startDate)
            }
          >
            Apply{" "}
          </MDButton>
          {(startDate || endDate) && (
            <MDButton disabled={loading} onClick={onClearClick}>
              Clear{" "}
            </MDButton>
          )}
        </Grid>
      </Grid>
      <MDBox
        sx={{
          position: "relative",
          paddingTop: "15px",
          paddingBottom: {
            xs: "150px",
            sm: "95px",
          },
        }}
      >
        <DataTable
          noBorders
          table={{ columns, rows }}
          canSearch={false}
          entriesPerPage={false}
        />
      </MDBox>
    </>
  );
};

AccountStatement.propTypes = {
  userId: PropTypes.string,
};

AccountStatement.defaultProps = {
  userId: "",
};

export default AccountStatement;
